/* eslint-disable prettier/prettier */
/* prettier-ignore-file */

import { useState, useEffect, useCallback } from 'react';

import {
	coachingService,
	LearnerUpcomingLessons,
	CreateMentoringLessonPayload,
	CreateMentoringLessonResponse,
	MarkAttendanceSinglePayload,
	MarkAttendanceBulkPayload,
	MarkAttendanceBulkResponse,
} from '../services/coachingService';

export const useCoaching = (learnerId: string, limit: number = 2) => {
	const [upcomingLessons, setUpcomingLessons] =
		useState<LearnerUpcomingLessons | null>(null);

	const [loading, setLoading] = useState(false);

	const [error, setError] = useState<string | null>(null);

	/* =========================
       CREATE LESSON STATES
    ========================= */

	const [createLessonLoading, setCreateLessonLoading] = useState(false);

	const [createLessonError, setCreateLessonError] = useState<string | null>(
		null
	);

	const [createdLesson, setCreatedLesson] =
		useState<CreateMentoringLessonResponse | null>(null);

	/* =========================
       ATTENDANCE STATES
    ========================= */

	const [attendanceLoading, setAttendanceLoading] = useState(false);

	const [attendanceError, setAttendanceError] = useState<string | null>(null);

	const [bulkAttendanceResponse, setBulkAttendanceResponse] =
		useState<MarkAttendanceBulkResponse | null>(null);

	const fetchUpcomingLessons = useCallback(async () => {
		if (!learnerId) return;

		try {
			setLoading(true);
			setError(null);

			const data = await coachingService.getLearnerUpcomingLessons(
				learnerId,
				limit
			);

			setUpcomingLessons(data);
		} catch (err: any) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [learnerId, limit]);

	useEffect(() => {
		fetchUpcomingLessons();
	}, [fetchUpcomingLessons]);

	/* =========================
       NEW HOOK FUNCTION
    ========================= */

	const createMentoringLesson = async (
		payload: CreateMentoringLessonPayload
	) => {
		try {
			setCreateLessonLoading(true);
			setCreateLessonError(null);

			const data = await coachingService.createMentoringLesson(payload);

			setCreatedLesson(data);

			return data;
		} catch (err: any) {
			setCreateLessonError(err.message);

			throw err;
		} finally {
			setCreateLessonLoading(false);
		}
	};

	/* =========================
       MARK SINGLE ATTENDANCE
    ========================= */

	const markAttendanceSingle = async (
		sessionId: string,
		userId: string,
		payload: MarkAttendanceSinglePayload
	) => {
		try {
			setAttendanceLoading(true);

			setAttendanceError(null);

			await coachingService.markAttendanceSingle(
				sessionId,
				userId,
				payload
			);
		} catch (err: any) {
			setAttendanceError(err.message);

			throw err;
		} finally {
			setAttendanceLoading(false);
		}
	};

	/* =========================
       MARK BULK ATTENDANCE
    ========================= */

	const markAttendanceBulk = async (
		sessionId: string,
		payload: MarkAttendanceBulkPayload
	) => {
		try {
			setAttendanceLoading(true);

			setAttendanceError(null);

			const data = await coachingService.markAttendanceBulk(
				sessionId,
				payload
			);

			setBulkAttendanceResponse(data);

			return data;
		} catch (err: any) {
			setAttendanceError(err.message);

			throw err;
		} finally {
			setAttendanceLoading(false);
		}
	};

	return {
		upcomingLessons,
		loading,
		error,
		refetch: fetchUpcomingLessons,

		/* NEW RETURNS */
		createMentoringLesson,
		createLessonLoading,
		createLessonError,
		createdLesson,

		/* ATTENDANCE */
		markAttendanceSingle,
		markAttendanceBulk,
		attendanceLoading,
		attendanceError,
		bulkAttendanceResponse,
	};
};
